import {
  DEFAULT_DATATABLE_PAGINATION,
  DEFAULT_QUERY_LIMIT,
  DEFAULT_QUERY_MAX,
  REGIONS_WITH_CURRENCY,
  ROUTE_PATHS,
  symbolFromCode,
} from "@client-shared/lib/constants";
import i18n from "@client-shared/lib/i18n";
import { isSameObject } from "@client-shared/lib/util";
import { handleDisplayErrorData } from "@client-shared/services/api/utilities/handleDisplayErrorData";
import { InvoiceStats, PurchaseOrderInvoice } from "@shared/types";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useLocation } from "react-router";
import * as api from "@client-shared/services/api";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useVendorContext } from "@client-shared/providers/vendor";
import {
  reducer,
  setFetching,
  setInitialized,
  setInvoiceDetails,
  setInvoiceStats,
  setLoading,
  setPagination,
  setParams,
  setSuccess,
} from "./action";
import { PayoutContextState, PayoutState, UsePayoutUtils } from "./types";

const initialState: PayoutState = {
  invoices: [],
  isInitialized: false,
  isLoading: false,
  isSuccess: false,
  isProcessing: false,
  limit: DEFAULT_QUERY_LIMIT,
  max: DEFAULT_QUERY_MAX,
  errors: [],
  pagination: {
    pageSize: DEFAULT_DATATABLE_PAGINATION.pageSize,
    pageIndex: DEFAULT_DATATABLE_PAGINATION.pageIndex,
  },
  invoiceStats: {
    toBePaidAmount: 0,
    unPostedAmount: 0,
    currencySymbol: REGIONS_WITH_CURRENCY.BUS.symbol,
  },
};

export const PayoutContext = React.createContext<PayoutContextState>([initialState, () => {}, {} as UsePayoutUtils]);

export const PayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [authState] = useSFDCAuthState();
  const [{ initialized: isVendorInitialized, currentVendor }] = useVendorContext();

  //Get current location to check pathname
  const location = useLocation();
  const isPayoutPath = React.useMemo(() => location.pathname.includes(ROUTE_PATHS.PAYOUTS), [location.pathname]);

  const fetchInvoices = React.useCallback(async () => {
    const data = await api.getInvoiceDetails();
    return { invoices: data };
  }, []);

  //Fetch Invoices
  const { data, isLoading, error, isSuccess, isFetching } = useQuery({
    queryKey: ["invoices"],
    queryFn: fetchInvoices,
    enabled: state.isInitialized,
  });

  const initialize = React.useCallback(() => {
    if (!currentVendor) return;
    const { pagination, params } = state;
    const newParams = {
      ...params,
      limit: params?.limit ?? DEFAULT_QUERY_LIMIT,
      vendorKey: currentVendor.vendorKey,
    };
    if (!isSameObject(state.pagination, pagination)) {
      dispatch(setPagination({ ...pagination, pageIndex: pagination?.pageIndex ?? 0, pageSize: pagination?.pageSize ?? 20 }));
    }
    if (!isSameObject(state.params, newParams)) {
      dispatch(setParams(newParams));
    }
    if (isPayoutPath && isVendorInitialized && authState && !state.isInitialized) {
      dispatch(setInitialized(true));
    }
  }, [authState, currentVendor, isPayoutPath, isVendorInitialized, state]);

  //Set the loading state
  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading]);

  //Set the success state
  useEffect(() => {
    dispatch(setSuccess(isSuccess));
  }, [isSuccess]);

  // Set the fetching state
  useEffect(() => {
    dispatch(setFetching(isFetching));
  }, [isFetching]);

  //set the invoice details
  useEffect(() => {
    if (data?.invoices) {
      const invoices = data.invoices;
      const invoiceStats = calculatePaymentinfo(invoices);
      dispatch(setInvoiceDetails(invoices));
      dispatch(setInvoiceStats(invoiceStats));
    }
  }, [data?.invoices, dispatch]);

  React.useEffect(() => {
    if (error) {
      handleDisplayErrorData(i18n.t("apiErrorMessages.fetchInvoices.message"), i18n.t("apiErrorMessages.fetchInvoices.title"), error);
    }
  }, [error]);

  const usePayoutUtils: UsePayoutUtils = React.useMemo(() => {
    return {
      initialize,
    };
  }, [initialize]);

  const contextValue: PayoutContextState = React.useMemo(() => [state, dispatch, usePayoutUtils], [state, usePayoutUtils]);
  return <PayoutContext.Provider value={contextValue}>{children}</PayoutContext.Provider>;
};

export const calculatePaymentinfo = (invoices: PurchaseOrderInvoice[]): InvoiceStats => {
  let totalUnpaidCents = 0;
  let totalUnpostedLineCents = 0;

  for (const invoice of invoices) {
    if (invoice.invoiceStatus === "Unpaid") {
      const amount = Number(invoice.invoiceAmount);
      totalUnpaidCents += Math.round(amount * 100);
    }
    if (invoice.invoiceStatus === "Unposted") {
      for (const item of invoice.invoiceLines) {
        const amount = Number(item.orderAmount);
        totalUnpostedLineCents += Math.round(amount * 100);
      }
    }
  }
  const invoice = invoices.find((invoice) => invoice.invoiceStatus !== "Unposted");
  const currencySymbol = symbolFromCode(invoice?.currencyCode ?? "USD");
  return {
    toBePaidAmount: totalUnpaidCents / 100,
    unPostedAmount: totalUnpostedLineCents / 100,
    currencySymbol,
  };
};
